import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../auth/msalConfig';
import {
  MdClose,
  MdRefresh,
  MdCheckCircle,
  MdWarningAmber,
  MdCode,
  MdInfoOutline,
  MdCloudUpload,
  MdUploadFile,
  MdInsertDriveFile,
  MdDeleteOutline,
  MdTableChart,
} from '../../utils/icons';
import { unwrapEnvelope, readEnvelope, StepApiError, fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from './stepApi';
import './AttributeConfig.css';
import './Tier2Config.css';

// ── Spec ───────────────────────────────────────────────────────────────────
// "Tier 2 Master Data" screen (sibling of the Attributes / Prompt panels). The
// STEP hierarchies workbook is the source of truth: the product team uploads
// 000_STEP_hierarchies.xlsx, the backend parses it, builds one Tier 2 slice per
// OpCo and publishes step-tier2.json. Entries are never hand-edited here.
// Backend: workflows/step_product_workflows/step_tier2_config_api.json
//   GET    {base}[?opco=X&include=hierarchies|block] -> artefact state
//   POST   {base}   multipart: file, updated_by, dry_run?, opcos?, base_revision?
//   PATCH  {base}   { opco, enabled, updated_by }
//   DELETE {base}?opco=X&updated_by=Y
// POST is replace-all: a publish drops OpCos absent from the upload.

// ── Types ────────────────────────────────────────────────────────────────────

interface Slice {
  key: string;
  opco: string;
  enabled: boolean;
  token_estimate: number;
  dropped_hierarchies: string[];
  entry_counts: Record<string, number>;
}

interface Artefact {
  artefact_version: string | null;
  artefact_revision: number | null;
  source_file: string | null;
  source_sha256: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  token_budget: number;
  opcos: string[];
  build_warnings: string[];
  slices: Slice[];
  total_entries: number | null;
}

// POST response — same shape for preview and publish; `published` is the difference.
interface UploadResult extends Artefact {
  dry_run: boolean;
  published: boolean;
}

interface Tier2ConfigProps {
  open: boolean;
  onClose: () => void;
  updatedBy: string;
}

// `uploading` carries a real byte percentage; `processing` is indeterminate — the
// server gives no signal between "bytes received" and "artefact built".
interface Progress {
  phase: 'uploading' | 'processing';
  pct: number;
}

type DetailKind = 'hierarchies' | 'block';
interface Detail {
  opco: string;
  kind: DetailKind;
  loading: boolean;
  error: string | null;
  hierarchies: Record<string, any[]> | null;
  block: string | null;
}

const DEFAULT_TOKEN_BUDGET = 7000;

// Sheet-key → label for the per-hierarchy entry counts.
const HIERARCHY_LABEL: Record<string, string> = {
  paltypes: 'Pallet types',
  transpcat: 'Transport categories',
  mattypes: 'Material types',
  whouses: 'Warehouses',
  pckngtypes: 'Packaging types',
  mattaxtypes: 'Material tax types',
};
const hLabel = (k: string): string => HIERARCHY_LABEL[k] ?? k;

// ── Helpers ──────────────────────────────────────────────────────────────────

const asArray = (v: any): string[] => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);

const toSlice = (s: any): Slice => ({
  key: typeof s?.key === 'string' ? s.key : `tier2-${s?.opco ?? '?'}`,
  opco: typeof s?.opco === 'string' ? s.opco : '?',
  enabled: s?.enabled !== false,
  token_estimate: typeof s?.token_estimate === 'number' ? s.token_estimate : 0,
  dropped_hierarchies: asArray(s?.dropped_hierarchies),
  entry_counts: s?.entry_counts && typeof s.entry_counts === 'object' ? s.entry_counts : {},
});

const toArtefact = (d: any): Artefact => ({
  artefact_version: typeof d?.artefact_version === 'string' ? d.artefact_version : null,
  artefact_revision: typeof d?.artefact_revision === 'number' ? d.artefact_revision : null,
  source_file: typeof d?.source_file === 'string' ? d.source_file : null,
  source_sha256: typeof d?.source_sha256 === 'string' ? d.source_sha256 : null,
  uploaded_by: typeof d?.uploaded_by === 'string' ? d.uploaded_by : null,
  uploaded_at: typeof d?.uploaded_at === 'string' ? d.uploaded_at : null,
  token_budget: typeof d?.token_budget === 'number' ? d.token_budget : DEFAULT_TOKEN_BUDGET,
  opcos: asArray(d?.opcos),
  build_warnings: asArray(d?.build_warnings),
  slices: Array.isArray(d?.slices) ? d.slices.map(toSlice) : [],
  total_entries: typeof d?.stats?.total_entries === 'number' ? d.stats.total_entries : null,
});

const fmtWhen = (iso: string | null): string => {
  if (!iso) return '';
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]} ${m[2]}` : iso;
};

const fmtSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const totalEntries = (s: Slice): number =>
  Object.values(s.entry_counts).reduce((a, n) => a + (typeof n === 'number' ? n : 0), 0);

// The workbook is ~2.7 MB and the server then parses 12 sheets before answering,
// so the operator has to see both phases. fetch() cannot report upload progress —
// XHR can, hence the exception to the fetch() used everywhere else in this panel.
const postWorkbook = (
  url: string,
  token: string,
  fd: FormData,
  onProgress: (pct: number) => void,
  onUploaded: () => void,
): Promise<{ status: number; body: any }> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    // Bytes are all sent — everything after this is server-side parse/build time
    // of unknown duration.
    xhr.upload.onload = () => onUploaded();
    xhr.onload = () => {
      let body: any = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error body */ }
      resolve({ status: xhr.status, body });
    };
    xhr.onerror = () => reject(new Error('network error while uploading the workbook'));
    xhr.ontimeout = () => reject(new Error('the upload timed out'));
    xhr.send(fd);
  });

// ── Component ────────────────────────────────────────────────────────────────

const Tier2Config: React.FC<Tier2ConfigProps> = ({ open, onClose, updatedBy }) => {
  const configUrl = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL + '/webhook/step-tier2-config';

  const { instance, accounts } = useMsal();

  // Acquire an AAD access token for the API scope. APIM rejects requests without
  // it (401 Unauthorized). Returns null when a redirect was triggered (silent
  // acquisition failed) — caller must bail out.
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch {
      await instance.acquireTokenRedirect(apiRequest);
      return null;
    }
  }, [instance, accounts]);

  // Published artefact. null + notPublished = nothing published yet (GET 404).
  const [artefact, setArtefact] = useState<Artefact | null>(null);
  const [notPublished, setNotPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // Upload panel. The File stays in memory between preview and publish — there is
  // no staged artefact server-side, so Confirm re-sends the same workbook.
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<UploadResult | null>(null);
  const [busy, setBusy] = useState<'preview' | 'publish' | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const [detail, setDetail] = useState<Detail | null>(null);

  const flash = (msg: string) => {
    setSavedFlash(msg);
    setTimeout(() => setSavedFlash(null), 2500);
  };

  // ── Load current state ──
  const load = useCallback(async () => {
    if (!configUrl) { setLoadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }));
      if (res.status === 404) {
        // Nothing published yet (NOT_FOUND) is a normal first-run state, not an error.
        setArtefact(null);
        setNotPublished(true);
        return;
      }
      const data = await unwrapEnvelope(res); // artefact state
      setArtefact(toArtefact(data));
      setNotPublished(false);
    } catch (err: any) {
      setLoadError(err instanceof StepApiError && TRANSIENT_STATUS.has(err.status)
        ? TRANSIENT_MESSAGE
        : `Failed to load the Tier 2 artefact. (${err?.message ?? 'unknown error'})`);
      setArtefact(null);
      setNotPublished(false);
    } finally {
      setLoading(false);
    }
  }, [configUrl, acquireApiToken]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Reset the upload panel each time the modal opens — a stale preview from a
  // previous session must never be publishable.
  useEffect(() => {
    if (open) return;
    setFile(null);
    setPreview(null);
    setUploadError(null);
    setConfirmPublish(false);
    setConfirmDelete(null);
    setDetail(null);
  }, [open]);

  // Esc closes the detail view first, then the panel (only with no pending publish).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detail) { setDetail(null); return; }
      if (!preview) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, detail, preview, onClose]);

  // ── Client-side checks (the workbook itself can only be validated server-side) ──
  const fileIssue = useMemo((): string | null => {
    if (!file) return 'Choose the STEP hierarchies workbook (.xlsx) to upload.';
    if (!/\.xlsx$/i.test(file.name)) return `"${file.name}" is not an .xlsx workbook.`;
    if (!updatedBy.trim()) return 'No signed-in user — the upload cannot be attributed.';
    return null;
  }, [file, updatedBy]);

  const pickFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setConfirmPublish(false);
    setUploadError(null);
  };

  // ── Upload (POST — preview with dry_run=true, publish with dry_run=false) ──
  const upload = async (dryRun: boolean) => {
    if (fileIssue || !file) { setUploadError(fileIssue); return; }
    if (!configUrl) { setUploadError('REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'); return; }

    setBusy(dryRun ? 'preview' : 'publish');
    setUploadError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered

      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('updated_by', updatedBy.trim());
      if (dryRun) fd.append('dry_run', 'true');
      // `opcos` is deliberately never sent — the backend discovers every OpCo in the
      // workbook and builds a slice per OpCo. Scoping is a post-publish decision made
      // with the per-slice enable toggle, not an upload-time one.
      // Optimistic lock. A pre-upload artefact reports null — the API takes 0 for it.
      if (!dryRun) fd.append('base_revision', String(artefact?.artefact_revision ?? 0));

      setProgress({ phase: 'uploading', pct: 0 });
      const { status, body } = await postWorkbook(
        configUrl,
        token,
        fd,
        (pct) => setProgress({ phase: 'uploading', pct }),
        () => setProgress({ phase: 'processing', pct: 100 }),
      );
      let data: any;
      try {
        // { dry_run, published, artefact_revision, opcos, slices, warnings, … }
        data = readEnvelope(status, body);
      } catch (e) {
        // 409 VERSION_CONFLICT = someone published while this operator was previewing.
        if (e instanceof StepApiError && e.code === 'VERSION_CONFLICT') {
          const cur = e.details?.current_revision;
          setUploadError(`${e.message}${cur != null ? ` (server is at revision ${cur})` : ''} Reload, re-preview, then publish again.`);
          setPreview(null);
          setConfirmPublish(false);
          await load();
          return;
        }
        throw e;
      }

      const parsed: UploadResult = {
        ...toArtefact(data),
        dry_run: data?.dry_run === true,
        published: data?.published === true,
      };

      if (dryRun) {
        setPreview(parsed);
        setConfirmPublish(false);
      } else {
        // Published — drop the staged file, show the new live state.
        setPreview(null);
        setConfirmPublish(false);
        setFile(null);
        await load();
        flash(`Published revision ${parsed.artefact_revision ?? '—'}`);
      }
    } catch (err: any) {
      // Server messages are the only real validation — surface them verbatim.
      setUploadError(`${dryRun ? 'Preview' : 'Publish'} failed — nothing was written. ${err?.message ?? 'unknown error'}`);
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  // ── PATCH — enable/disable one slice ──
  const toggleSlice = async (opco: string, enabled: boolean) => {
    setRowBusy(opco);
    setLoadError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(configUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ opco, enabled, updated_by: updatedBy.trim() || 'unknown' }),
      }));
      await unwrapEnvelope(res); // { opco, enabled, artefact_revision }
      await load();
      flash(`${opco} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      setLoadError(`Could not ${enabled ? 'enable' : 'disable'} ${opco}. (${err?.message ?? 'unknown error'})`);
    } finally {
      setRowBusy(null);
    }
  };

  // ── DELETE — drop one slice ──
  const deleteSlice = async (opco: string) => {
    setRowBusy(opco);
    setConfirmDelete(null);
    setLoadError(null);
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const url = `${configUrl}?opco=${encodeURIComponent(opco)}&updated_by=${encodeURIComponent(updatedBy.trim() || 'unknown')}`;
      const res = await fetchWithRetry(() => fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }));
      await unwrapEnvelope(res); // { deleted, opcos, artefact_revision }
      await load();
      flash(`${opco} deleted`);
    } catch (err: any) {
      setLoadError(`Could not delete ${opco}. (${err?.message ?? 'unknown error'})`);
    } finally {
      setRowBusy(null);
    }
  };

  // ── GET ?include= — read-only entry / prompt-block detail ──
  const openDetail = async (opco: string, kind: DetailKind) => {
    setDetail({ opco, kind, loading: true, error: null, hierarchies: null, block: null });
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered
      const res = await fetchWithRetry(() => fetch(`${configUrl}?opco=${encodeURIComponent(opco)}&include=${kind}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      }));
      const data = await unwrapEnvelope(res); // { …, hierarchies? | block? }
      setDetail({
        opco,
        kind,
        loading: false,
        error: null,
        hierarchies: kind === 'hierarchies' && data?.hierarchies ? data.hierarchies : null,
        block: kind === 'block' && typeof data?.block === 'string' ? data.block : null,
      });
    } catch (err: any) {
      setDetail({ opco, kind, loading: false, error: err?.message ?? 'unknown error', hierarchies: null, block: null });
    }
  };

  if (!open) return null;

  const live = artefact;
  const budget = preview?.token_budget ?? live?.token_budget ?? DEFAULT_TOKEN_BUDGET;
  // OpCos the publish would drop: present in the live artefact, absent from this
  // workbook. Replace-all semantics make that silent otherwise.
  const dropped = preview ? (live?.opcos ?? []).filter((o) => !preview.opcos.includes(o)) : [];

  const renderSliceCard = (s: Slice, mode: 'preview' | 'live') => {
    const over = s.token_estimate > budget;
    const pct = Math.min(100, Math.round((s.token_estimate / Math.max(1, budget)) * 100));
    const busyRow = rowBusy === s.opco;
    return (
      <div key={s.key} className={`t2-slice${s.enabled ? '' : ' t2-slice--off'}`}>
        <div className="t2-slice-head">
          <span className="t2-slice-opco">{s.opco}</span>
          <span className="t2-slice-total">{totalEntries(s).toLocaleString()} entries</span>
          {s.dropped_hierarchies.length > 0 && (
            <span className="t2-badge t2-badge--drop" title="Dropped to stay inside the token budget — extraction falls back to Tier 3 for these">
              <MdWarningAmber aria-hidden="true" />dropped: {s.dropped_hierarchies.map(hLabel).join(', ')}
            </span>
          )}
          {mode === 'live' && (
            <div className="t2-slice-actions">
              <label className="t2-toggle" title={s.enabled
                ? 'Enabled — Tier 2 reference attributes are injected for this OpCo'
                : 'Disabled — Tier-2-dependent attributes are stripped from the prompt for this OpCo'}>
                <input
                  type="checkbox"
                  className="acfg-check"
                  checked={s.enabled}
                  disabled={busyRow}
                  onChange={(e) => toggleSlice(s.opco, e.target.checked)}
                />
                <span>{s.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
              <button className="acfg-icon-btn" onClick={() => openDetail(s.opco, 'hierarchies')}
                title="View entries" aria-label={`View ${s.opco} entries`}>
                <MdTableChart aria-hidden="true" />
              </button>
              <button className="acfg-icon-btn" onClick={() => openDetail(s.opco, 'block')}
                title="View the prompt block the model receives" aria-label={`View ${s.opco} prompt block`}>
                <MdCode aria-hidden="true" />
              </button>
              <button className="acfg-icon-btn acfg-icon-btn--del" onClick={() => setConfirmDelete(s.opco)}
                disabled={busyRow} title="Delete slice (prefer the toggle — it is reversible)"
                aria-label={`Delete ${s.opco} slice`}>
                <MdDeleteOutline aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        <div className="t2-tokens">
          <div className="t2-tokenbar">
            <div className={`t2-tokenbar-fill${over ? ' t2-tokenbar-fill--over' : ''}`} style={{ width: `${pct}%` }} />
          </div>
          <span className={`t2-token-num${over ? ' t2-token-num--over' : ''}`}>
            {s.token_estimate.toLocaleString()} / {budget.toLocaleString()} tokens
          </span>
        </div>

        <div className="t2-counts">
          {Object.entries(s.entry_counts).map(([k, n]) => (
            <span key={k} className="t2-count">
              <span className="t2-count-label">{hLabel(k)}</span>
              <strong>{typeof n === 'number' ? n.toLocaleString() : '—'}</strong>
            </span>
          ))}
        </div>

        {mode === 'live' && confirmDelete === s.opco && (
          <div className="t2-confirm">
            <p className="t2-confirm-title">Delete the {s.opco} slice?</p>
            <p className="t2-confirm-desc">
              The next extraction run for {s.opco} drops its Tier-2-dependent reference attributes. Restoring it
              needs a fresh workbook upload — <strong>Disabled</strong> does the same thing reversibly.
            </p>
            <div className="t2-confirm-actions">
              <button className="acfg-btn acfg-btn--ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="t2-btn-danger" onClick={() => deleteSlice(s.opco)}>Delete {s.opco}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="acfg-overlay" role="dialog" aria-modal="true" aria-label="Tier 2 Master Data configuration">
      <div className="acfg-backdrop" onClick={() => { if (!preview) onClose(); }} />
      <div className="acfg-panel">

        {/* ── Header ── */}
        <div className="acfg-head">
          <div className="acfg-head-left">
            <h2 className="acfg-title">Tier 2 Master Data</h2>
            <p className="acfg-subtitle">
              OpCo reference hierarchies injected into the extraction prompt — published from the STEP hierarchies workbook
            </p>
          </div>
          <button className="acfg-close" onClick={() => { if (!preview) onClose(); }} aria-label="Close"
            title={preview ? 'Publish or discard the preview first' : 'Close'} disabled={!!preview}>
            <MdClose aria-hidden="true" />
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="acfg-toolbar">
          <span className="t2-toolbar-note">
            <MdInfoOutline aria-hidden="true" />
            The workbook is the source of truth — a wrong value goes back to Master Data and gets re-uploaded.
          </span>
          <button className="acfg-btn acfg-btn--ghost pcfg-toolbar-right" onClick={load} disabled={loading || !!busy}
            title="Reload the published artefact">
            <MdRefresh aria-hidden="true" />Reload
          </button>
        </div>

        {/* ── Status line ── */}
        <div className="acfg-status">
          <span className="acfg-status-item">
            Revision <strong>{live?.artefact_revision ?? '—'}</strong>
          </span>
          <span className="acfg-status-sep">·</span>
          <span className="acfg-status-item">
            <strong>{live?.slices.length ?? 0}</strong> OpCo slice{(live?.slices.length ?? 0) === 1 ? '' : 's'}
          </span>
          {live?.total_entries != null && (
            <>
              <span className="acfg-status-sep">·</span>
              <span className="acfg-status-item"><strong>{live.total_entries.toLocaleString()}</strong> entries</span>
            </>
          )}
          {live?.source_file && (
            <>
              <span className="acfg-status-sep">·</span>
              <span className="acfg-status-item" title={live.source_sha256 ?? undefined}>
                from <strong>{live.source_file}</strong>
                {live.source_sha256 && <code className="t2-sha">{live.source_sha256.slice(0, 12)}</code>}
              </span>
            </>
          )}
          {live?.uploaded_by && (
            <>
              <span className="acfg-status-sep">·</span>
              <span className="acfg-status-item">
                by <strong>{live.uploaded_by}</strong>
                {live.uploaded_at && ` · ${fmtWhen(live.uploaded_at)}`}
              </span>
            </>
          )}
          {savedFlash && <span className="acfg-saved"><MdCheckCircle aria-hidden="true" />{savedFlash}</span>}
        </div>

        {/* ── Errors ── */}
        {loadError && (
          <div className="acfg-banner acfg-banner--error">
            <MdWarningAmber aria-hidden="true" />{loadError}
          </div>
        )}
        {uploadError && (
          <div className="acfg-banner acfg-banner--error">
            <MdWarningAmber aria-hidden="true" /><span className="t2-server-msg">{uploadError}</span>
          </div>
        )}

        {/* ── Body ── */}
        <div className="t2-body">

          {/* Upload panel */}
          <section className="t2-section">
            <h3 className="t2-section-title">Upload a hierarchies workbook</h3>

            <label
              className={`t2-drop${dragOver ? ' t2-drop--over' : ''}${file ? ' t2-drop--has' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) pickFile(f);
              }}
            >
              <input
                type="file"
                className="t2-drop-input"
                accept=".xlsx"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
              {file ? (
                <>
                  <MdInsertDriveFile aria-hidden="true" className="t2-drop-icon" />
                  <span className="t2-drop-file">{file.name}</span>
                  <span className="t2-drop-hint">{fmtSize(file.size)} · click or drop to replace</span>
                </>
              ) : (
                <>
                  <MdUploadFile aria-hidden="true" className="t2-drop-icon" />
                  <span className="t2-drop-file">Drop 000_STEP_hierarchies.xlsx here, or click to browse</span>
                  <span className="t2-drop-hint">.xlsx only · 16 MB max</span>
                </>
              )}
            </label>

            <p className="t2-scope-note">
              <MdInfoOutline aria-hidden="true" />
              Every OpCo in the workbook is detected automatically and gets its own slice. Turn individual OpCos
              off with the <strong>Enabled</strong> toggle after publishing.
            </p>

            {progress && (
              <div className="t2-progress" role="status" aria-live="polite">
                <div className="t2-progress-head">
                  <span className="t2-progress-phase">
                    {progress.phase === 'uploading'
                      ? `Uploading ${file?.name ?? 'workbook'}…`
                      : busy === 'publish'
                        ? 'Processing — parsing sheets, building slices, publishing…'
                        : 'Processing — parsing sheets, building slices…'}
                  </span>
                  <span className="t2-progress-pct">
                    {progress.phase === 'uploading' ? `${progress.pct}%` : 'working'}
                  </span>
                </div>
                <div className="t2-progress-bar">
                  <div
                    className={`t2-progress-fill${progress.phase === 'processing' ? ' t2-progress-fill--indet' : ''}`}
                    style={progress.phase === 'uploading' ? { width: `${progress.pct}%` } : undefined}
                  />
                </div>
                <span className="t2-progress-note">
                  {progress.phase === 'uploading'
                    ? 'Sending the workbook to the server.'
                    : 'The server reports no intermediate steps — this takes a few seconds on a full workbook.'}
                </span>
              </div>
            )}

            <div className="t2-upload-actions">
              <button className="acfg-btn acfg-btn--add" onClick={() => upload(true)}
                disabled={!!fileIssue || !!busy}
                title={fileIssue ?? 'Build the artefact and report what would be published — writes nothing'}>
                <MdCloudUpload aria-hidden="true" />{busy === 'preview' ? 'Processing…' : 'Preview'}
              </button>
              {fileIssue && <span className="t2-inline-hint">{fileIssue}</span>}
              {!fileIssue && !busy && !preview && (
                <span className="t2-inline-hint">Preview writes nothing — the live artefact only changes on Confirm &amp; Publish.</span>
              )}
            </div>
          </section>

          {/* Preview result */}
          {preview && (
            <section className="t2-section t2-section--preview">
              <h3 className="t2-section-title">
                Preview · nothing published yet
                <span className="t2-badge t2-badge--dry">dry run</span>
              </h3>

              <div className="t2-prev-meta">
                <span>Would publish revision <strong>{preview.artefact_revision ?? '—'}</strong></span>
                <span className="acfg-status-sep">·</span>
                <span><strong>{preview.slices.length}</strong> slice{preview.slices.length === 1 ? '' : 's'}</span>
                <span className="acfg-status-sep">·</span>
                <span><strong>{(preview.total_entries ?? 0).toLocaleString()}</strong> entries</span>
                <span className="acfg-status-sep">·</span>
                <span title={preview.source_sha256 ?? undefined}>
                  {preview.source_file}
                  {preview.source_sha256 && <code className="t2-sha">{preview.source_sha256.slice(0, 12)}</code>}
                </span>
              </div>

              <div className="t2-discovered">
                <span className="t2-discovered-label">Discovered OpCos</span>
                {preview.opcos.map((o) => <span key={o} className="t2-chip">{o}</span>)}
                <span className="t2-inline-hint">Detected from the workbook — confirm the list before publishing.</span>
              </div>

              {dropped.length > 0 && (
                <div className="acfg-banner acfg-banner--warn t2-warn-block">
                  <MdWarningAmber aria-hidden="true" />
                  <div>
                    <strong>{dropped.join(', ')} would be dropped.</strong> {dropped.length === 1 ? 'It is' : 'They are'} live
                    today but absent from this workbook, and a publish replaces the whole artefact. Those OpCos lose their
                    Tier 2 reference attributes on the next extraction run.
                  </div>
                </div>
              )}

              {preview.build_warnings.length > 0 && (
                <div className="acfg-banner acfg-banner--warn t2-warn-block">
                  <MdWarningAmber aria-hidden="true" />
                  <div>
                    <strong>{preview.build_warnings.length} build warning{preview.build_warnings.length === 1 ? '' : 's'}</strong>
                    {' '}— expected, not failures. IDs are kept verbatim.
                    <ul className="t2-warn-list">
                      {preview.build_warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              <div className="t2-slices">{preview.slices.map((s) => renderSliceCard(s, 'preview'))}</div>

              {confirmPublish ? (
                <div className="t2-confirm">
                  <p className="t2-confirm-title">Publish this artefact?</p>
                  <p className="t2-confirm-desc">
                    This replaces the live artefact for <strong>every</strong> OpCo — {preview.opcos.join(', ')} — and takes effect
                    on the next extraction run. OpCos not in this list lose their Tier 2 slice.
                  </p>
                  <div className="t2-confirm-actions">
                    <button className="acfg-btn acfg-btn--ghost" onClick={() => setConfirmPublish(false)}>Cancel</button>
                    <button className="acfg-btn acfg-btn--save" onClick={() => upload(false)} disabled={!!busy}>
                      <MdCloudUpload aria-hidden="true" />{busy === 'publish' ? 'Publishing…' : 'Publish'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="t2-upload-actions">
                  <button className="acfg-btn acfg-btn--ghost" onClick={() => { setPreview(null); setConfirmPublish(false); }}
                    disabled={!!busy}>
                    Discard preview
                  </button>
                  <button className="acfg-btn acfg-btn--save" onClick={() => setConfirmPublish(true)} disabled={!!busy}>
                    <MdCloudUpload aria-hidden="true" />Confirm &amp; Publish
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Published state */}
          <section className="t2-section">
            <h3 className="t2-section-title">Published slices</h3>
            {loading ? (
              <div className="acfg-empty">Loading the Tier 2 artefact…</div>
            ) : notPublished ? (
              <div className="acfg-empty">
                No Tier 2 artefact published yet.
                <br />Upload a hierarchies workbook above to publish the first one.
              </div>
            ) : !live || live.slices.length === 0 ? (
              <div className="acfg-empty">The published artefact has no OpCo slices.</div>
            ) : (
              <>
                {live.build_warnings.length > 0 && (
                  <div className="acfg-banner acfg-banner--warn t2-warn-block">
                    <MdWarningAmber aria-hidden="true" />
                    <div>
                      <strong>{live.build_warnings.length} build warning{live.build_warnings.length === 1 ? '' : 's'} on the live artefact</strong>
                      <ul className="t2-warn-list">
                        {live.build_warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  </div>
                )}
                <div className="t2-slices">{live.slices.map((s) => renderSliceCard(s, 'live'))}</div>
              </>
            )}
          </section>
        </div>

        {/* ── Footer ── */}
        <div className="acfg-footer">
          <p className="acfg-footer-hint">
            A publish replaces the whole artefact and applies on the next extraction run. Disabling or deleting a slice
            strips that OpCo's Tier-2-dependent attributes from the prompt — a safe state, not an error.
          </p>
          <div className="acfg-footer-actions">
            <button className="acfg-btn acfg-btn--ghost" onClick={() => { if (!preview) onClose(); }} disabled={!!preview}
              title={preview ? 'Publish or discard the preview first' : 'Close'}>
              Close
            </button>
          </div>
        </div>

        {/* ── Detail view (read-only) ── */}
        {detail && (
          <div className="t2-detail" role="dialog" aria-modal="true"
            aria-label={detail.kind === 'block' ? `${detail.opco} prompt block` : `${detail.opco} entries`}>
            <div className="t2-detail-head">
              <div>
                <h3 className="acfg-title">
                  {detail.opco} · {detail.kind === 'block' ? 'Prompt block' : 'Entries'}
                </h3>
                <p className="acfg-subtitle">
                  {detail.kind === 'block'
                    ? 'The exact text the extraction agent injects for this OpCo'
                    : 'Read-only — corrections go through the Master Data workbook'}
                </p>
              </div>
              <button className="acfg-close" onClick={() => setDetail(null)} aria-label="Close detail">
                <MdClose aria-hidden="true" />
              </button>
            </div>
            <div className="t2-detail-body">
              {detail.loading ? (
                <div className="acfg-empty">Loading {detail.opco}…</div>
              ) : detail.error ? (
                <div className="acfg-banner acfg-banner--error">
                  <MdWarningAmber aria-hidden="true" />{detail.error}
                </div>
              ) : detail.kind === 'block' ? (
                <pre className="t2-block">{detail.block ?? '(empty block)'}</pre>
              ) : detail.hierarchies ? (
                Object.entries(detail.hierarchies).map(([k, entries]) => (
                  <div key={k} className="t2-hier">
                    <h4 className="t2-hier-title">
                      {hLabel(k)}
                      <span className="t2-hier-count">{Array.isArray(entries) ? entries.length : 0}</span>
                    </h4>
                    <pre className="t2-block t2-block--sm">{JSON.stringify(entries, null, 2)}</pre>
                  </div>
                ))
              ) : (
                <div className="acfg-empty">No entries returned for {detail.opco}.</div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Tier2Config;
